import React from 'react';
import { Box } from '@mui/material';
import { ColumnLayoutProps } from './ColumnLayout.props';

/**
 * ColumnLayout - Simple vertical flexbox layout component
 *
 * @example
 * ```tsx
 * <ColumnLayout spacing="md" alignItems="center">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 *   <div>Item 3</div>
 * </ColumnLayout>
 * ```
 */
export const ColumnLayout: React.FC<ColumnLayoutProps> = ({
  children,
  spacing,
  alignItems,
  justifyContent,
  fullHeight,
  fullWidth,
  sx,
  ...boxProps
}) => {
  // Convert spacing name to MUI theme spacing multiplier
  const getSpacingMultiplier = (
    spacingValue: ColumnLayoutProps['spacing']
  ): number | undefined => {
    if (spacingValue === undefined) return undefined;
    if (typeof spacingValue === 'number') return spacingValue;

    switch (spacingValue) {
      case 'none':
        return 0;
      case 'xs':
        return 0.5;
      case 'sm':
        return 1;
      case 'md':
        return 2;
      case 'lg':
        return 3;
      case 'xl':
        return 4;
      case '2xl':
        return 6;
      default:
        return undefined;
    }
  };

  const spacingMultiplier = getSpacingMultiplier(spacing);

  return (
    <Box
      {...boxProps}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(alignItems && { alignItems }),
        ...(justifyContent && { justifyContent }),
        ...(spacingMultiplier !== undefined && { gap: spacingMultiplier }),
        ...(fullWidth && { width: '100%' }),
        ...(fullHeight && { height: '100%' }),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default ColumnLayout;
